package acupoint.components

import com.jingfangpai.common.data.model.IdName
import kotlinext.js.js
import kotlinx.html.js.onClickFunction
import react.*
import react.dom.attrs
import react.dom.jsStyle
import react.dom.span
import react.router.dom.useHistory
import urlBase

external interface ClickableAcupointTextProps : RProps {
    var text: String
    var acupointIdNames: Map<String, String>
    var currentAcupointName: String?
}

/**
 * Makes acupoint names in text clickable and navigable.
 * Similar to ClickableAcupointText in Android version.
 */
val ClickableAcupointText = functionalComponent<ClickableAcupointTextProps> { props ->
    val history = useHistory()

    // Sort acupoint names by length (longest first) to avoid partial matches
    val sortedAcupoints = props.acupointIdNames.keys.sortedByDescending { it.length }

    // Find all acupoint occurrences and their positions
    data class AcupointSpan(val start: Int, val end: Int, val id: String, val name: String)
    val spans = mutableListOf<AcupointSpan>()

    sortedAcupoints.forEach { acupointName ->
        val acupointId = props.acupointIdNames[acupointName]
        if (!acupointId.isNullOrBlank()) {
            // Skip current acupoint name - don't make it clickable
            if (props.currentAcupointName != null && 
                acupointName.equals(props.currentAcupointName, ignoreCase = true)) {
                return@forEach
            }

            var startIndex = 0
            while (true) {
                val index = props.text.indexOf(acupointName, startIndex, ignoreCase = true)
                if (index == -1) break

                // Check if this position overlaps with existing span
                val overlaps = spans.any {
                    !(index >= it.end || index + acupointName.length <= it.start)
                }

                if (!overlaps) {
                    spans.add(AcupointSpan(index, index + acupointName.length, acupointId, acupointName))
                }
                startIndex = index + acupointName.length
            }
        }
    }

    // Sort spans by start position
    spans.sortBy { it.start }

    // Build HTML with clickable spans
    fun buildHtml(): String {
        val html = StringBuilder()
        var lastIndex = 0

        spans.forEach { span ->
            // Add text before this span
            if (span.start > lastIndex) {
                html.append(props.text.substring(lastIndex, span.start))
            }

            // Add the acupoint name with click handler
            val encodedId = span.id.replace("'", "\\'")
            html.append("<span class=\"clickable-acupoint\" data-acupoint-id=\"${span.id}\" ")
            html.append("style=\"color: #1890ff; cursor: pointer; font-weight: 500;\">")
            html.append(span.name)
            html.append("</span>")

            lastIndex = span.end
        }

        // Add remaining text
        if (lastIndex < props.text.length) {
            html.append(props.text.substring(lastIndex))
        }

        return html.toString()
    }

    // Handle click on acupoint links
    fun handleClick(event: dynamic) {
        val target = event.target
        
        // Check if clicked element or its parent has the clickable-acupoint class
        var element = target
        while (element != null && element !== event.currentTarget) {
            if (element.className?.contains("clickable-acupoint") == true) {
                val acupointId = element.getAttribute("data-acupoint-id")
                if (acupointId != null) {
                    event.preventDefault()
                    event.stopPropagation()
                    history.push("$urlBase/learning/acupoint/detail/$acupointId")
                }
                break
            }
            element = element.parentElement
        }
    }

    span {
        attrs["dangerouslySetInnerHTML"] = js {
            __html = buildHtml()
        }
        attrs.onClickFunction = { event ->
            handleClick(event)
        }
        attrs.jsStyle = js {
            lineHeight = "1.6"
        }
    }
}

/**
 * Helper function to remove HTML tags from text
 */
fun removeHtmlTags(html: String): String {
    return html.replace(Regex("<[^>]*>"), "")
        .replace("&nbsp;", " ")
        .replace("&lt;", "<")
        .replace("&gt;", ">")
        .replace("&amp;", "&")
        .replace("&quot;", "\"")
        .replace("&#39;", "'")
        .trim()
}
